/**
 * DashboardDateRangeSelector — header date control for the home dashboard.
 *
 * Layout: 4 quick-range pills (Today / This Week / This Month / Last Month)
 * + a Custom pill that opens the shared CustomRangePicker calendar. Picking
 * the custom range writes `{ key: "custom", fromDate, toDate }` into
 * RangeContext; widget hooks pick the change up via their cache keys.
 */

import * as React from "react";
import { CustomRangePicker } from "./CustomRangePicker";
import {
  DashboardRangeKey,
  DASHBOARD_RANGE_LABELS,
  useDashboardRangeControl,
} from "./RangeContext";

const QUICK_KEYS: ReadonlyArray<Exclude<DashboardRangeKey, "custom">> = [
  "today",
  "this_week",
  "this_month",
  "last_month",
];

const PILL_BASE =
  "px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap";
const PILL_SELECTED = "bg-blue-600 text-white shadow-sm";
const PILL_IDLE = "bg-gray-100 text-gray-700 hover:bg-gray-200";

export function DashboardDateRangeSelector() {
  const { range, setRange } = useDashboardRangeControl();
  const isCustom = range.key === "custom";

  return (
    <div className="flex flex-wrap items-center gap-1">
      {QUICK_KEYS.map((key) => (
        <button
          key={key}
          type="button"
          className={`${PILL_BASE} ${range.key === key ? PILL_SELECTED : PILL_IDLE}`}
          onClick={() => setRange({ key })}
        >
          {DASHBOARD_RANGE_LABELS[key]}
        </button>
      ))}
      <CustomRangePicker
        selected={isCustom}
        fromDate={isCustom ? range.fromDate : undefined}
        toDate={isCustom ? range.toDate : undefined}
        onCommit={(fromDate, toDate) =>
          setRange({ key: "custom", fromDate, toDate })
        }
        triggerClassName={`${PILL_BASE} ${isCustom ? PILL_SELECTED : PILL_IDLE}`}
      />
    </div>
  );
}
